import { useEffect, useRef, useState } from "react";
import { IconPlayerPause, IconPlayerPlay, IconX } from "@tabler/icons-react";
import { resolveMediaUrl } from "@utils/media/mediaUrl";
import { previewAiVoiceRequest } from "@services/ai/aiApi";
import { capitalizeLabel, findAiVoice } from "@utils/mode_tool/pointEditorHelpers";

export function AiSpeakerConfiguration({
  number,
  settings,
  catalog,
  voices,
  voiceGroups = [],
  selectedVoice,
  selectedVoiceDetails,
  isLoadingCatalog,
  isLoadingVoices,
  disabledVoice = "",
  onSettingChange,
  onVoiceChange,
  searchTerm = "",
  onSearchChange,
  hasPreviousPage = false,
  hasNextPage = false,
  onPreviousPage,
  onNextPage,
  onRemove,
}) {
  const isSpeakerTwo = number === 2;
  const [isVoiceTableOpen, setIsVoiceTableOpen] = useState(!selectedVoice);
  const [openGroupId, setOpenGroupId] = useState("");
  const [previewVoiceId, setPreviewVoiceId] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewAudioRef = useRef(null);

  useEffect(() => {
    setIsVoiceTableOpen(!selectedVoice);
  }, [selectedVoice]);

  const groupedSelectedVoice = voiceGroups
    .flatMap((group) => Array.isArray(group?.voices) ? group.voices : [])
    .find((voice) => String(voice?.id || "") === String(selectedVoice || ""));
  const selectedVoiceData =
    findAiVoice(voices, selectedVoice) ||
    groupedSelectedVoice ||
    selectedVoiceDetails;
  const selectedVoicePreviewId = String(
    selectedVoiceData?.id || selectedVoice || "",
  );
  const openGroup = voiceGroups.find((group) => group.id === openGroupId);
  const displayedGroups = openGroupId ? (openGroup ? [openGroup] : []) : voiceGroups;

  useEffect(() => {
    const audio = previewAudioRef.current;
    return () => {
      audio?.pause();
      if (audio) audio.src = "";
    };
  }, []);

  async function toggleVoicePreview(voice) {
    const voiceId = String(voice?.id || "");
    if (!voiceId || isPreviewLoading) return;
    const audio = previewAudioRef.current;
    if (!audio) return;

    if (previewVoiceId === voiceId && !audio.paused) {
      audio.pause();
      setPreviewVoiceId("");
      return;
    }

    setPreviewVoiceId(voiceId);
    setIsPreviewLoading(true);
    try {
      audio.pause();
      const response = await previewAiVoiceRequest(voiceId);
      const previewUrl = String(response?.body?.multimedia?.url || "");
      if (!previewUrl) throw new Error("The voice preview audio is unavailable.");
      audio.src = resolveMediaUrl(previewUrl);
      audio.currentTime = 0;
      await audio.play();
      setPreviewVoiceId(voiceId);
    } catch (error) {
      setPreviewVoiceId("");
    } finally {
      setIsPreviewLoading(false);
    }
  }
  return (
    <section
      className={`mode-route-description-ai-speaker-card ${
        isSpeakerTwo ? "is-speaker-two" : "is-speaker-one"
      }`}
    >
      <div className="mode-route-description-ai-speaker-card-heading">
        <span className="mode-route-description-ai-speaker-label">
          Speaker {number}
        </span>
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove Speaker 2"
            title="Remove Speaker 2"
          >
            <IconX size={16} />
          </button>
        ) : null}
      </div>

      <div className="mode-route-description-ai-speaker-options">
        {[
          { key: "language", label: "Language" },
          { key: "gender", label: "Gender" },
          { key: "pitch", label: "Pitch" },
          { key: "style", label: "Style" },
          { key: "useCase", label: "Use case" },
        ].map((field) => (
          <label key={field.key} className="mode-route-description-ai-field">
            <span>{field.label}</span>
            <select
              className="field-input"
              value={settings[field.key] || ""}
              disabled={isLoadingCatalog}
              onChange={(event) => {
                setIsVoiceTableOpen(true);
                onSettingChange(field.key, event.target.value);
              }}
            >
              <option value="">
                {isLoadingCatalog ? "Loading..." : `Select ${field.label.toLowerCase()}`}
              </option>
              {(catalog[field.key] || []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <div className="mode-route-description-ai-speaker-voice-result">
        <div className="mode-route-description-ai-voice-result-heading">
          <span>Voices</span>
          <input
            type="search"
            value={searchTerm}
            onFocus={() => setIsVoiceTableOpen(true)}
            onChange={(event) => {
              setIsVoiceTableOpen(true);
              onSearchChange?.(event.target.value);
            }}
            placeholder="Search voice name"
            aria-label={`Search Speaker ${number} voices`}
          />
        </div>
        {selectedVoice && !isVoiceTableOpen ? (
          <div className="mode-route-description-ai-selected-voice">
            <div>
              <strong>{selectedVoiceData?.name || selectedVoice}</strong>
              <small>
                {capitalizeLabel(
                  selectedVoiceData?.summary ||
                    selectedVoiceData?.description ||
                    selectedVoiceData?.persona ||
                    "Voice preview available",
                )} · {capitalizeLabel(selectedVoiceData?.gender || settings.gender)} · {capitalizeLabel(selectedVoiceData?.pitch || settings.pitch)} pitch
              </small>
            </div>
            <div className="mode-route-description-ai-selected-voice-actions">
              <button
                type="button"
                className="mode-route-description-ai-voice-select"
                aria-label={`Preview ${selectedVoiceData?.name || selectedVoice}`}
                title={`Preview ${selectedVoiceData?.name || selectedVoice}`}
                onClick={() =>
                  toggleVoicePreview({
                    ...(selectedVoiceData || {}),
                    id: selectedVoicePreviewId,
                  })
                }
              >
                {isPreviewLoading && previewVoiceId === selectedVoicePreviewId ? (
                  <span className="mode-route-description-ai-preview-loading">…</span>
                ) : previewVoiceId === selectedVoicePreviewId ? (
                  <IconPlayerPause size={12} />
                ) : (
                  <IconPlayerPlay size={12} />
                )}
              </button>
              <button type="button" onClick={() => setIsVoiceTableOpen(true)}>
                Change voice
              </button>
            </div>
          </div>
        ) : (
          <>
        <div className="mode-route-description-ai-voice-groups">
          {isLoadingVoices ? (
            <p className="mode-route-description-ai-voice-table-empty">Searching voices...</p>
          ) : voiceGroups.length ? (
            <>
              {openGroup ? (
                <div className="mode-route-description-ai-group-detail-heading">
                  <button type="button" onClick={() => setOpenGroupId("")} aria-label="Back to voice groups">
                    ←
                  </button>
                  <strong>{openGroup.name}</strong>
                  <span>{openGroup.total || openGroup.voices.length} voices</span>
                </div>
              ) : null}
              {displayedGroups.map((group) => {
                const groupVoices = openGroup ? group.voices : group.voices.slice(0, 3);
                return (
                  <section key={group.id} className="mode-route-description-ai-voice-group">
                    {!openGroup ? (
                      <div className="mode-route-description-ai-voice-group-heading">
                        <strong>{group.name}</strong>
                        {group.voices.length > 3 ? (
                          <button type="button" onClick={() => setOpenGroupId(group.id)}>
                            View all {group.total || group.voices.length}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {groupVoices.map((voice) => {
                      const isSelected = voice.id === selectedVoice;
                      const isDisabled = voice.id === disabledVoice;
                      return (
                        <div
                          key={voice.id}
                          className={`mode-route-description-ai-voice-group-row ${isSelected ? "is-selected" : ""}`}
                          role="button"
                          tabIndex={isDisabled ? -1 : 0}
                          aria-disabled={isDisabled}
                          onClick={() => {
                            if (isDisabled) return;
                            onVoiceChange(voice.id);
                            setIsVoiceTableOpen(false);
                          }}
                          onKeyDown={(event) => {
                            if (isDisabled) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onVoiceChange(voice.id);
                              setIsVoiceTableOpen(false);
                            }
                          }}
                        >
                          <div>
                            <strong>{voice.name}</strong>
                            <span>
                              {capitalizeLabel(voice.summary || voice.description)} · {capitalizeLabel(voice.gender)} · {capitalizeLabel(voice.pitch)} pitch
                            </span>
                          </div>
                          <button
                            type="button"
                            className="mode-route-description-ai-voice-select"
                            disabled={isDisabled}
                            aria-label={`Preview ${voice.name}`}
                            title={`Preview ${voice.name}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleVoicePreview(voice);
                            }}
                          >
                            {isPreviewLoading && previewVoiceId === voice.id ? (
                              <span className="mode-route-description-ai-preview-loading">…</span>
                            ) : previewVoiceId === voice.id ? (
                              <IconPlayerPause size={12} />
                            ) : (
                              <IconPlayerPlay size={12} />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </section>
                );
              })}
            </>
          ) : (
            <p className="mode-route-description-ai-voice-table-empty">No voices found.</p>
          )}
        </div>
          </>
        )}
        <audio
          ref={previewAudioRef}
          hidden
          onEnded={() => setPreviewVoiceId("")}
        />
      </div>
    </section>
  );
}

